import {useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useFormContext} from 'react-hook-form';
import ChekinPro from '../../ChekinPro';
import {LocalStorage, useResetFieldIfTypeChanged} from '@guestapp/core';
import {usePreloadGuestData} from '../usePreloadGuestData';
import {useReservation} from 'context/reservation';
import {useBuildIVPayload, useGuestGroup, useGuestMutation} from 'hooks';
import {useOpenModals} from 'context/openModals';
import {AddFormV2Type, useSchema} from 'context/guest';
import {
  CustomForm,
  FieldsErrorResponse,
  Guest,
  isRequiredFormField,
  isActiveFormField,
} from '@guestapp/sdk';
import {SHOWED_SUCCESS_REGISTER_COMPLETE_MODAL} from 'utils/constants';
import {
  FORM_NAMES,
  FORM_NAMES_DTO,
  FormNamesOldType,
  FormTypes,
  getIsLeaderGuest,
} from 'utils/guestForm';
import {useIVStore} from 'store/identityVerification';
import {useErrorModal, useIsMounted, useModalControls} from '../common';
import {useSummary} from 'context/summary';
import {getPayloadDate} from 'utils/common';
import {buildGuestPayload} from './buildGuestPayload';
import {useErrorHandler} from 'components/GuestFormFields/useErrorHandler';
import {usePreloadPoliceData} from 'components/GuestFormFields/usePreloadPoliceData';
import {useUpsellingFlow} from 'components/upselling/useUpsellingFlow';
import {useMutateLivenessDetection} from 'hooks/useMutateLivenessDetection';
import {useDefaultFieldsMapper} from './useDefaultFieldsMapper';
import {sortFormNamesByOrder} from './utils/sortFormNamesByOrder';
import SignBlock from 'components/SignBlock';
import IncompleteRegisterModal from 'components/IncompleteRegisterModal';
import DiscardChangesModalSheet from 'components/ModalSheets/DiscardChanges';
import StatusModal, {StatusModalStates} from 'components/StatusModal';
import {CustomField as CustomFieldComponent} from './CustomField';
import {GuestRegisteringModal} from 'components/GuestRegisteringModal';
import {DefaultField} from './DefaultField';
import {GroupedFields} from './GroupedFields';
import {AgreementClausesBox} from 'components/AgreementClausesBox';

type UseResetCityIfCountryChangedProps = {
  cityFieldName: FORM_NAMES;
  countryFieldName: FORM_NAMES;
};
const useResetCityIfCountryChanged = ({
  cityFieldName,
  countryFieldName,
}: UseResetCityIfCountryChangedProps) => {
  const {watch, getValues, resetField} = useFormContext<FormTypes>();
  const countryValue = watch(countryFieldName)?.value;

  useEffect(() => {
    const city = getValues(cityFieldName);

    if (city?.value) {
      resetField(cityFieldName);
    }
  }, [countryValue, cityFieldName, resetField, getValues]);
};

type GuestFormFieldsProps = {
  housingCountry: string;
  id?: string;
  onGuestRegistered?: (data: Guest) => Promise<unknown>;
};

function useGuestFormFields({
  id: guestId,
  housingCountry,
  onGuestRegistered,
}: GuestFormFieldsProps) {
  const isMounted = useIsMounted();
  const isEdition = Boolean(guestId);
  const {formState, watch, handleSubmit, getValues, setError, trigger, reset} =
    useFormContext<FormTypes>();

  const canRevalidateForm =
    formState.isSubmitted && !formState.isSubmitSuccessful && !formState.isSubmitting;

  const {setIsFormTouched} = useOpenModals();
  const {ErrorModal, displayError, closeErrorModal} = useErrorModal();
  const {data: reservation, reservationId, housing} = useReservation();
  const {data: summary} = useSummary();

  useEffect(() => {
    const isThereDirtyField = Boolean(Object.keys(formState.dirtyFields || {})?.length);
    setIsFormTouched(isThereDirtyField);
  }, [setIsFormTouched, formState]);

  const fullNameCurrentGuestRef = useRef('');
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [guestRegistrationStatus, setGuestRegistrationStatus] = useState(
    StatusModalStates.IDLE,
  );
  const {resetAllIVState} = useIVStore();
  const {t} = useTranslation();

  const nationality = watch(FORM_NAMES.nationality)?.value;
  const documentType = watch(FORM_NAMES.document_type)?.value;
  const birthPlaceCountry = watch(FORM_NAMES.birth_place_country)?.value;
  const arrivedFromCountry = watch(FORM_NAMES.arrived_from_country)?.value;
  const nextDestinationCountry = watch(FORM_NAMES.next_destination_country)?.value;
  const documentExpeditionCountry = watch(FORM_NAMES.document_expedition_country)?.value;
  const arrivedFromDistrict = watch(FORM_NAMES.arrived_from_district)?.value;
  const residenceCountry = watch(FORM_NAMES.residence_country)?.value;
  const residenceProvince = watch(FORM_NAMES.residence_province)?.value;
  const citizenship = watch(FORM_NAMES.citizenship)?.value;
  const birthDate = watch(FORM_NAMES.birth_date);
  const invoiceLeaderType = watch(FORM_NAMES.invoice_leader_type)?.value;
  const recipientCodeType = watch(FORM_NAMES.recipient_code_type)?.value;

  const {
    formFields,
    setBirthCountry,
    setArrivalCountry,
    setArrivalDistrict,
    setNextDestinationCountry,
    setNationality,
    setDocumentExpeditionCountry,
    setResidenceCountry,
    setResidenceProvince,
    setCitizenship,
    setDocumentType,
    setBirthDate,
    setIsLeaderGuest,
    setInvoiceLeaderType,
    setRecipientCodeType,
    isGuestSchemaLoading,
    isGuestSchemaLoaded,
    isGuestSchemaFetching,
    customForm,
    customFormsVersion,
    agreementClauses,
  } = useSchema();

  const {clearPersistedStorageFormData} = usePreloadGuestData();
  const {preloadPoliceData} = usePreloadPoliceData({
    getValues,
    reset,
    disabled: isEdition,
  });
  const {isLeaderGuest} = useGuestGroup();

  useLayoutEffect(() => {
    preloadPoliceData();
  }, [preloadPoliceData]);

  const isLoadingForm = isGuestSchemaLoading && !isGuestSchemaLoaded;

  const isDisabledField = isGuestSchemaLoading || isGuestSchemaFetching;

  const closeGuestRegistrationStatusModal = useCallback(() => {
    setGuestRegistrationStatus(StatusModalStates.IDLE);
  }, []);

  const {
    isOpen: isDataIncompleteModalOpen,
    openModal: openDataIncompleteModalOpen,
    closeModal: closeDataIncompleteModalOpen,
  } = useModalControls();

  useEffect(() => {
    setIsLeaderGuest?.(getIsLeaderGuest());
  }, [setIsLeaderGuest]);

  useEffect(() => {
    setNationality?.(nationality);
  }, [nationality, setNationality]);
  useEffect(() => {
    setDocumentType?.(documentType);
  }, [documentType, setDocumentType]);
  useEffect(() => {
    setArrivalCountry?.(arrivedFromCountry);
  }, [arrivedFromCountry, setArrivalCountry]);
  useEffect(() => {
    setArrivalDistrict?.(arrivedFromDistrict);
  }, [arrivedFromDistrict, setArrivalDistrict]);
  useEffect(() => {
    setBirthCountry?.(birthPlaceCountry);
  }, [birthPlaceCountry, setBirthCountry]);
  useEffect(() => {
    setDocumentExpeditionCountry?.(documentExpeditionCountry);
  }, [documentExpeditionCountry, setDocumentExpeditionCountry]);
  useEffect(() => {
    setNextDestinationCountry?.(nextDestinationCountry);
  }, [nextDestinationCountry, setNextDestinationCountry]);
  useEffect(() => {
    setResidenceCountry?.(residenceCountry);
  }, [residenceCountry, setResidenceCountry]);
  useEffect(() => {
    setResidenceProvince?.(residenceProvince);
  }, [residenceProvince, setResidenceProvince]);
  useEffect(() => {
    setCitizenship?.(citizenship);
  }, [citizenship, setCitizenship]);
  useEffect(() => {
    const dateString = getPayloadDate(birthDate);
    setBirthDate?.(dateString);
  }, [birthDate, setBirthDate]);
  useEffect(() => {
    setInvoiceLeaderType?.(invoiceLeaderType);
  }, [invoiceLeaderType, setInvoiceLeaderType]);
  useEffect(() => {
    setRecipientCodeType?.(recipientCodeType);
  }, [recipientCodeType, setRecipientCodeType]);

  useEffect(
    function revalidateFormOnDisplayFieldsChange() {
      if (canRevalidateForm) {
        void trigger();
      }
    },
    [trigger, formFields, canRevalidateForm],
  );

  useResetFieldIfTypeChanged<FormTypes>({
    fieldName: FORM_NAMES.birth_place_city,
    type: formFields?.[FORM_NAMES.birth_place_city]?.type,
  });

  useResetFieldIfTypeChanged<FormTypes>({
    fieldName: FORM_NAMES.arrived_from_municipality,
    type: formFields?.[FORM_NAMES.arrived_from_municipality]?.type,
  });

  useResetCityIfCountryChanged({
    cityFieldName: FORM_NAMES.birth_place_city,
    countryFieldName: FORM_NAMES.birth_place_country,
  });

  useResetFieldIfTypeChanged<FormTypes>({
    fieldName: FORM_NAMES.residence_city,
    type: formFields?.[FORM_NAMES.residence_city]?.type,
  });
  useResetCityIfCountryChanged({
    cityFieldName: FORM_NAMES.residence_city,
    countryFieldName: FORM_NAMES.residence_country,
  });

  useResetFieldIfTypeChanged({
    fieldName: FORM_NAMES.document_expedition_city,
    type: formFields?.[FORM_NAMES.document_expedition_city]?.type,
  });
  useResetCityIfCountryChanged({
    cityFieldName: FORM_NAMES.document_expedition_city,
    countryFieldName: FORM_NAMES.document_expedition_country,
  });

  const {checkUpsellingFlow, isUpsellingFlowWorking} = useUpsellingFlow(
    housing?.id,
    summary,
  );

  const isRegisteredModalOpen =
    guestRegistrationStatus !== StatusModalStates.IDLE && !isUpsellingFlowWorking;
  const closeRegisteredModal = useCallback(() => {
    resetAllIVState();
    closeGuestRegistrationStatusModal();
  }, [closeGuestRegistrationStatusModal, resetAllIVState]);

  const {mutate: mutateLivenessResultData} = useMutateLivenessDetection({});
  const livenessResultData = useIVStore(state => state.livenessResultData);
  const isIvFailed = useIVStore(state => state.isIvFailed);

  const {GuestErrorModal, displayFieldErrors} = useErrorHandler({
    setError,
    fields: formFields,
    onDataIncomplete: openDataIncompleteModalOpen,
  });

  const handleError = (error: FieldsErrorResponse) => {
    if (!isMounted.current) {
      return;
    }

    if (error.status_code !== 400) {
      closeGuestRegistrationStatusModal();
      displayError(error);
      return;
    }

    if ((error as FieldsErrorResponse).errors) {
      displayFieldErrors(error as FieldsErrorResponse);
    } else {
      displayFieldErrors(error);
    }
    closeGuestRegistrationStatusModal();
  };

  const guestMutation = useGuestMutation({
    onSuccess: async (data: Guest) => {
      await onGuestRegistered?.(data);

      checkUpsellingFlow(data);
      setGuestRegistrationStatus(StatusModalStates.SUCCESS);
      ChekinPro.events.onGuestRegistered(data);

      if (livenessResultData) {
        mutateLivenessResultData({
          reservation_id: reservationId ?? '',
          is_detection_passed: livenessResultData?.livenessPassed ?? false,
          detected_head_movements: livenessResultData?.completedActions ?? [],
          liveness_video_link: livenessResultData.livenessVideoUrl,
          performed_by: livenessResultData.performedBy,
          guest_id: data?.id ?? '',
        });
      }
      clearPersistedStorageFormData();
    },
    onError: handleError,
  });

  const {getIVPayload} = useBuildIVPayload();

  const onSubmit = useCallback(
    async (formData: FormTypes, signature: string, guestId?: string) => {
      closeErrorModal();

      fullNameCurrentGuestRef.current = `${formData[FORM_NAMES.name]} ${
        formData[FORM_NAMES.surname]
      }`;
      setGuestRegistrationStatus(StatusModalStates.LOADING);

      const identityVerificationPayload = getIVPayload();
      const isLeader = getIsLeaderGuest();
      const extraFormData = {
        is_leader: isLeader,
        is_iv_failed: isIvFailed,
      };
      const payload = buildGuestPayload({
        signature,
        data: {
          ...formData,
          ...extraFormData,
        },
        reservationId: reservationId,
        identityVerificationPayload,
        customForm: customForm as CustomForm,
        formFields,
      });

      if (guestMutation && reservationId) {
        guestMutation.mutate({
          id: guestId,
          payload,
        });
      }

      LocalStorage.remove(SHOWED_SUCCESS_REGISTER_COMPLETE_MODAL);
    },
    [
      closeErrorModal,
      getIVPayload,
      reservationId,
      customForm,
      guestMutation,
      formFields,
      isIvFailed,
    ],
  );

  const handleSubmitForm = useCallback(
    (signature: string) => {
      handleSubmit(
        (data: FormTypes) => onSubmit(data, signature, guestId),
        openDataIncompleteModalOpen,
      )();
    },
    [onSubmit, handleSubmit, guestId, openDataIncompleteModalOpen],
  );

  const defaultFieldsMapper = useDefaultFieldsMapper({
    guestId,
    isDisabledField,
    housingCountry,
  });

  const displayingFieldNames = useMemo(() => {
    if (Object.values(formFields).length > 0) {
      const filteredFieldNames = (
        Object.keys(defaultFieldsMapper) as FORM_NAMES[]
      ).filter(fieldName => {
        return isActiveFormField(formFields?.[fieldName]);
      });

      return sortFormNamesByOrder(filteredFieldNames, formFields);
    } else {
      return [];
    }
  }, [defaultFieldsMapper, formFields]);

  const renderFormFields = () => {
    const isAddingGuestLeader = isLeaderGuest;
    const customFields = hasCustomForm
      ? customForm?.fields_set.filter(customField => customField.field.is_custom)
      : [];

    const customFieldsJsx = customFields?.map(
      ({field, is_required, is_leaders_field}) => {
        const fieldName = FORM_NAMES_DTO[field.name as FormNamesOldType];

        const required =
          is_required || isRequiredFormField(formFields?.[fieldName])
            ? t('this_field_is_required')
            : false;
        const hidden = isActiveFormField(formFields?.[fieldName]) === false;

        if (hidden || (!isAddingGuestLeader && is_leaders_field)) {
          return null;
        }
        return (
          <CustomFieldComponent
            key={field.id}
            field={field}
            disabled={isDisabledField}
            required={required}
          />
        );
      },
    );

    const defaultFieldsJsx = displayingFieldNames.map(fieldName => (
      <DefaultField
        key={fieldName}
        formFieldsMapper={defaultFieldsMapper}
        fieldName={fieldName}
        formFields={formFields}
      />
    ));

    return customFieldsJsx ? [defaultFieldsJsx, ...customFieldsJsx] : defaultFieldsJsx;
  };

  const renderFormFieldsV2 = () => (
    <GroupedFields
      defaultFieldsMapper={defaultFieldsMapper}
      formFields={formFields as AddFormV2Type}
    />
  );

  const hasCustomForm = Boolean(customForm?.id);

  const hasSignature = useMemo(() => {
    return isActiveFormField(formFields?.[FORM_NAMES.signature]);
  }, [formFields]);

  const SignatureField = useMemo(() => {
    return (
      <>
        <StatusModal
          open={isRegisteredModalOpen}
          state={guestRegistrationStatus}
          maxWidth="350px"
        >
          <GuestRegisteringModal
            name={fullNameCurrentGuestRef.current}
            onClose={closeRegisteredModal}
            state={guestRegistrationStatus}
            isEdition={isEdition}
          />
        </StatusModal>
        <SignBlock
          agreementClauses={<AgreementClausesBox clauses={agreementClauses} />}
          onSubmit={handleSubmitForm}
          automatedSignature={reservation?.auto_signature}
          hasSignature={hasSignature}
          isSignatureRequired={isRequiredFormField(formFields?.[FORM_NAMES.signature])}
          setIsSignModalOpen={setIsSignModalOpen}
          isSignModalOpen={isSignModalOpen}
          openDataIncompleteModalOpen={openDataIncompleteModalOpen}
        />
      </>
    );
  }, [
    isRegisteredModalOpen,
    guestRegistrationStatus,
    closeRegisteredModal,
    isEdition,
    handleSubmitForm,
    reservation?.auto_signature,
    hasSignature,
    formFields,
    isSignModalOpen,
    openDataIncompleteModalOpen,
    agreementClauses,
  ]);

  const FormModals = useCallback(() => {
    return (
      <>
        <IncompleteRegisterModal
          open={isDataIncompleteModalOpen}
          onClose={closeDataIncompleteModalOpen}
        />
        <DiscardChangesModalSheet />
        <ErrorModal />
        <GuestErrorModal />
      </>
    );
  }, [
    isDataIncompleteModalOpen,
    closeDataIncompleteModalOpen,
    ErrorModal,
    GuestErrorModal,
  ]);

  return {
    isLoadingForm,
    renderFormFields: customFormsVersion === 'V2' ? renderFormFieldsV2 : renderFormFields,
    FormModals,
    SignatureField,
    hasSignature,
    isDataIncompleteModalOpen,
    isLoadedForm: isGuestSchemaLoaded,
  };
}

export {useGuestFormFields};
